import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import { Node as K8sNode } from '../k8s-analyzer-types';
import { calculatePercentage } from './k8s-analyzer-nodes-list.utils';

interface K8sAnalyzerNodesListStatisticsProps {
    nodes: K8sNode[];
}

export function K8sAnalyzerNodesListStatistics({ nodes }: K8sAnalyzerNodesListStatisticsProps) {
    const calculateStatistics = () => {
        let totalNodeMemoryUsage = 0;
        let totalNodeMemoryCapacity = 0;
        let totalPodMemoryUsage = 0;
        let totalPodMemoryCapacity = 0;
        let totalPodCount = 0;

        nodes.forEach(node => {
            // Node memory statistics
            totalNodeMemoryUsage += parseFloat(node.metrics.memory.usage);
            totalNodeMemoryCapacity += parseFloat(node.metrics.memory.capacity);

            // Pod memory statistics
            if (node.pods_list) {
                totalPodCount += node.pods_list.length;
                node.pods_list.forEach(pod => {
                    totalPodMemoryUsage += parseFloat(pod.metrics.memory.usage);
                    totalPodMemoryCapacity += parseFloat(pod.metrics.memory.capacity);
                });
            }
        });

        const avgNodeMemoryUsagePercentage = (totalNodeMemoryUsage / totalNodeMemoryCapacity) * 100;
        const avgPodMemoryUsagePercentage = totalPodCount > 0 
            ? (totalPodMemoryUsage / totalPodMemoryCapacity) * 100 
            : 0;

        return {
            nodeCount: nodes.length,
            podCount: totalPodCount,
            avgNodeMemoryUsage: avgNodeMemoryUsagePercentage.toFixed(2),
            avgPodMemoryUsage: avgPodMemoryUsagePercentage.toFixed(2),
            totalNodeMemoryUsage: (totalNodeMemoryUsage / 1024).toFixed(2), // Convert to GB
            totalNodeMemoryCapacity: (totalNodeMemoryCapacity / 1024).toFixed(2), // Convert to GB
            totalPodMemoryUsage: (totalPodMemoryUsage / 1024).toFixed(2), // Convert to GB
            totalPodMemoryCapacity: (totalPodMemoryCapacity / 1024).toFixed(2), // Convert to GB
        };
    };

    const stats = calculateStatistics();

    return (
        <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Node Memory Statistics
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Nodes:
                                </Typography>
                                <Typography variant="body2">
                                    {stats.nodeCount}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Average Memory Usage:
                                </Typography>
                                <Typography variant="body2" color={Number(stats.avgNodeMemoryUsage) > 80 ? 'error.main' : 'inherit'}>
                                    {stats.avgNodeMemoryUsage}%
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Memory Usage:
                                </Typography>
                                <Typography variant="body2">
                                    {stats.totalNodeMemoryUsage}GB / {stats.totalNodeMemoryCapacity}GB
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Pod Memory Statistics
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Pods:
                                </Typography>
                                <Typography variant="body2">
                                    {stats.podCount}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Average Memory Usage:
                                </Typography>
                                <Typography variant="body2" color={Number(stats.avgPodMemoryUsage) > 80 ? 'error.main' : 'inherit'}>
                                    {stats.avgPodMemoryUsage}%
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Memory Usage:
                                </Typography>
                                <Typography variant="body2">
                                    {stats.totalPodMemoryUsage}GB / {stats.totalPodMemoryCapacity}GB
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
} 